import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { subscriptionGateway } from '../index';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { EmptyState } from '../../../shared/EmptyState';
import { RetryableError } from '../../../shared/RetryableError';
import { useBilling } from './BillingContext';
import { safeExternalUrl } from './hostedRedirect';
import { formatCents, formatUtcDate, invoiceStatusLabel } from './money';
import { useResource } from './useResource';

const PAGE_SIZE = 10;

/** Provider-hosted invoice and PDF links, shown only when they are plain https URLs. */
export function InvoiceLinks({ hostedInvoiceUrl, invoicePdfUrl, label }: { hostedInvoiceUrl: string | null; invoicePdfUrl: string | null; label: string }) {
  const hosted = safeExternalUrl(hostedInvoiceUrl);
  const pdf = safeExternalUrl(invoicePdfUrl);
  if (!hosted && !pdf) return <span className="muted">No links yet</span>;
  return (
    <span className="billing-links">
      {hosted && <a href={hosted} target="_blank" rel="noopener noreferrer" aria-label={`Open ${label} on the secure invoice page (opens in a new tab)`}>Open invoice</a>}
      {pdf && <a href={pdf} target="_blank" rel="noopener noreferrer" aria-label={`Download ${label} as a PDF (opens in a new tab)`}>PDF</a>}
    </span>
  );
}

export function InvoicesSection() {
  const { refreshToken } = useBilling();
  const [page, setPage] = useState(1);
  const load = useCallback(
    (signal: AbortSignal) => subscriptionGateway.listInvoices({ page, pageSize: PAGE_SIZE }, signal),
    [page, refreshToken], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const { state, reload } = useResource(load, 'Could not load invoices.');

  return (
    <section aria-labelledby="billing-invoices-heading" className="billing-section" data-testid="billing-invoices">
      <h3 id="billing-invoices-heading">Invoices</h3>

      {state.status === 'loading' && <p className="muted" role="status">Loading invoices…</p>}
      {state.status === 'denied' && <AccessDeniedNotice requiredPermissionLabel="Billing: View" />}
      {state.status === 'unavailable' && <p className="muted">Billing is not available in this environment, so there are no invoices to show.</p>}
      {state.status === 'error' && <RetryableError message={state.message} onRetry={reload} />}

      {state.status === 'ready' && state.data.items.length === 0 && (
        <EmptyState message="No invoices yet. Your first invoice appears here after your first payment." />
      )}

      {state.status === 'ready' && state.data.items.length > 0 && (
        <>
          <div className="billing-table-wrap">
            <table className="billing-table" aria-label="Invoices">
              <thead>
                <tr>
                  <th scope="col">Invoice</th>
                  <th scope="col">Issued</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="billing-table__amount">Subtotal</th>
                  <th scope="col" className="billing-table__amount">Tax</th>
                  <th scope="col" className="billing-table__amount">Total</th>
                  <th scope="col">Links</th>
                </tr>
              </thead>
              <tbody>
                {state.data.items.map((invoice) => {
                  const name = invoice.number ?? 'invoice';
                  return (
                    <tr key={invoice.id}>
                      <th scope="row"><Link to={`/settings/billing/invoices/${encodeURIComponent(invoice.id)}`} aria-label={`View details for ${name}`}>{invoice.number ?? 'Invoice'}</Link></th>
                      <td>{formatUtcDate(invoice.issuedAtUtc)}</td>
                      <td>{invoiceStatusLabel(invoice.status)}</td>
                      <td className="billing-table__amount">{formatCents(invoice.subtotalCents)}</td>
                      <td className="billing-table__amount">{formatCents(invoice.taxCents)}</td>
                      <td className="billing-table__amount">{formatCents(invoice.totalCents)}</td>
                      <td><InvoiceLinks hostedInvoiceUrl={invoice.hostedInvoiceUrl} invoicePdfUrl={invoice.invoicePdfUrl} label={name} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager page={state.data.page} pageSize={state.data.pageSize} totalCount={state.data.totalCount} onPage={setPage} noun="invoices" />
        </>
      )}
    </section>
  );
}

export function Pager({
  page, pageSize, totalCount, onPage, noun,
}: {
  page: number;
  pageSize: number;
  totalCount: number;
  onPage: (page: number) => void;
  noun: string;
}) {
  const pages = Math.max(1, Math.ceil(totalCount / pageSize));
  if (pages <= 1) return null;
  return (
    <nav className="pagination" aria-label={`${noun} pages`}>
      <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
      <span>Page {page} of {pages}</span>
      <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
    </nav>
  );
}
